import { BaseSVG } from '../../shared/BaseSVG';
import { toAbsoluteUrl } from '../../utils/toAbsoluteUrl';
import { translate } from '../Internationalization';
import images from '../../config/local/images';
import React from 'react';

/**
 * @typedef {{
 * 	address?: {streetAddress1?: string | null},
 * 	id: string,
 * 	title?: string | null,
 * }} SelectorProperty
 */

/**
 * @typedef {{
 * 	className: string,
 * 	formatPropertyLabel?: (property: SelectorProperty) => string,
 * 	idPrefix: string,
 * 	onSelect: (propertyId: string) => void,
 * 	propertyList: SelectorProperty[],
 * 	selectedProperty?: SelectorProperty | null,
 * }} PropertyDropdownSelectorProps
 */

export default class PropertyDropdownSelector extends React.Component {
	/**
	 * @param {PropertyDropdownSelectorProps} props
	 */
	constructor(props) {
		super(props);

		this.propertySelector = React.createRef();
		this.handleDocumentClick = this.#handleDocumentClick.bind(this);
		this.handleDocumentKeyDown = this.#handleDocumentKeyDown.bind(this);
		this.handleToggleClick = this.#handleToggleClick.bind(this);
		this.renderPropertyOption = this.#renderPropertyOption.bind(this);

		this.state = {
			isActive: false,
		};
	}

	componentDidMount() {
		document.addEventListener('click', this.handleDocumentClick);
		document.addEventListener('keydown', this.handleDocumentKeyDown);
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleDocumentClick);
		document.removeEventListener('keydown', this.handleDocumentKeyDown);
	}

	/**
	 * @param {Event} event
	 * @returns {void}
	 */
	#handleDocumentClick(event) {
		const isClickInside = this.propertySelector.current?.contains(
			event.target
		);

		if (!isClickInside) {
			this.setState({ isActive: false });
		}
	}

	/**
	 * @param {KeyboardEvent} event
	 * @returns {void}
	 */
	#handleDocumentKeyDown(event) {
		if (event.key === 'Escape') {
			this.setState({ isActive: false });
		}
	}

	/**
	 * @returns {void}
	 */
	#handleToggleClick() {
		this.setState(
			/**
			 * @param {{isActive: boolean}} prevState
			 * @returns {{isActive: boolean}}
			 */
			(prevState) => ({
				isActive: !prevState.isActive,
			})
		);
	}

	/**
	 * @param {SelectorProperty} property
	 * @returns {string}
	 */
	#getPropertyLabel(property) {
		if (this.props.formatPropertyLabel) {
			return this.props.formatPropertyLabel(property);
		}

		return property.address?.streetAddress1 ?? property.title ?? '';
	}

	/**
	 * @param {SelectorProperty} property
	 * @returns {JSX.Element}
	 */
	#renderPropertyOption(property) {
		return (
			<li className="menu-item" key={property.id}>
				<button
					className="link w-full"
					onClick={() => {
						this.props.onSelect(property.id);
						this.setState({ isActive: false });
					}}
					type="button"
				>
					<span className="min-w-0 flex-1 pr-3 text-left">
						{this.#getPropertyLabel(property)}
					</span>
					<span className="action flex-shrink-0">
						{translate('global.actions.select')}
						<i className="icon icon-sm">
							<BaseSVG path={images.icons.homeowners} />
						</i>
					</span>
				</button>
			</li>
		);
	}

	/** @returns {JSX.Element | null} */
	#renderMenuList() {
		const menuList = this.props.propertyList
			.filter(
				/**
				 * @param {SelectorProperty} property
				 * @returns {boolean}
				 */
				(property) => property.id !== this.props.selectedProperty?.id
			)
			.map(this.renderPropertyOption);

		return menuList.length ? (
			<ul
				aria-labelledby={`${this.props.idPrefix}-button`}
				className="menu"
				id={`${this.props.idPrefix}-drop-down`}
				role="region"
			>
				{menuList}
			</ul>
		) : null;
	}

	render() {
		const selectedPropertyAddress = this.props.selectedProperty
			? this.#getPropertyLabel(this.props.selectedProperty)
			: '';

		if (!this.props.propertyList.length || !this.props.selectedProperty) {
			return null;
		}

		return (
			<section
				className={this.props.className}
				ref={this.propertySelector}
			>
				<button
					aria-controls={`${this.props.idPrefix}-drop-down`}
					aria-expanded={this.state.isActive ? 'true' : 'false'}
					aria-label={translate(
						'aria_labels.property_drop_down_selector',
						selectedPropertyAddress
					)}
					className={`selector ${
						this.state.isActive ? 'active' : null
					}`}
					id={`${this.props.idPrefix}-button`}
					onClick={this.handleToggleClick}
					type="button"
				>
					<span className="label flex-shrink-0">
						<img
							alt=""
							aria-hidden="true"
							className="icon"
							src={toAbsoluteUrl(images.homeowners.header.house)}
						/>
						<span className="uppercase">
							{translate('global.header.property')}
						</span>
					</span>
					<span className="current-item min-w-0 flex-1 justify-end">
						<span className="value block">
							{selectedPropertyAddress}
						</span>
						<i aria-hidden="true" className="icon icon-xs">
							<BaseSVG path={images.homeowners.header.arrow} />
						</i>
					</span>
				</button>
				{this.#renderMenuList()}
			</section>
		);
	}
}

PropertyDropdownSelector.defaultProps = {
	className: 'fancy-dropdown',
	idPrefix: 'property-selector',
};
